"use client";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useState, use } from "react";
import AppStoreBadgeIcon from "@/components/_icons/appStoreBadgeIcon";
import GooglePlayBadgeIcon from "@/components/_icons/googlePlayBadgeIcon";
import CloverIcon from "../_icons/cloverIcon";
import { usePathname } from "next/navigation";
import { MerchantFooter } from "../merchantFooter/merchantFooter";
import { Service } from "@/dto/service";
import { Merchant } from "@/dto/merchant";
import { useCookies } from "react-cookie";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import { CloverLinks, getCloverLinks } from "@/utils/cloverLinks";
import { LanguageSelector } from "@/components/languageSelector/languageSelector";
import { ServiceType } from "@/constants/enum";
import { useMerchantFeatureFlag } from "@/utils/useMerchantFeatureFlag";
import { FEATURE_FLAG_LOCALIZATION_ON_COLO2 } from "@/utils/constants";
import { useNativeActions } from "@/utils/useNativeActions";

export interface FooterProps {
  googlePlayUrl: string;
  appStoreUrl: string;
  policiesPromise?: Promise<any> | null;
  merchant: Merchant | null;
}

export const Footer = (props: FooterProps) => {
  const t = useTranslations("Footer");
  const pathname = usePathname();
  const [classNames, setClassNames] = useState([
    "w-full",
    "bg-[#F8F8F8]",
    "text-[#000000]",
    "flex",
    "flex-col",
    "gap-[36px]",
    "px-4",
    "sm:px-10",
    "pb-2",
    "text-[15px]",
  ]);
  const { policiesPromise, merchant } = props;
  const { isCmaApp } = useNativeActions();

  const isCheckoutPage: boolean =
    pathname?.includes("/checkout") ||
    pathname?.includes("/guest-checkout") ||
    false;
  const isProfilePage: boolean =
    pathname?.includes("/customer-profile") ||
    pathname?.includes("/verify-email") ||
    false;
  const isConfirmationPage: boolean =
    pathname?.includes("/confirmation") || false;
  const isOrderHistoryPage: boolean = pathname?.includes("/my-orders") || false;
  const [cookies] = useCookies(["scanToOrderInfo"]);
  const { scanToOrderInfo } = cookies;
  const isScanToOrderFlow = !!scanToOrderInfo;
  let isRunningTabsEnabled = false;
  const localizationEnabledFlag = useMerchantFeatureFlag(
    merchant,
    FEATURE_FLAG_LOCALIZATION_ON_COLO2
  );
  const rawLocale = useLocale();
  const links: CloverLinks = getCloverLinks(
    localizationEnabledFlag ? rawLocale : merchant?.locale
  );

  useEffect(() => {
    if (pathname === "/guest-checkout" || pathname === "/checkout") {
      setClassNames((prevClasses) => {
        const tempClasses = prevClasses.filter(
          (className) => className !== "pb-2"
        );
        return [...tempClasses, "pb-16"];
      });
    } else if (pathname === "/confirmation") {
      if (isScanToOrderFlow && !isRunningTabsEnabled) {
        setClassNames((prevClasses) => {
          const tempClasses = prevClasses.filter(
            (className) => className !== "pb-2"
          );
          if (tempClasses.includes("mb-16")) {
            return tempClasses;
          }
          return [...tempClasses, "mb-16"];
        });
      }
    } else {
      setClassNames((prevClasses) => {
        const tempClasses = prevClasses.filter((name) => name !== "pb-16");
        return [...tempClasses, "pb-2"];
      });
    }
  }, [pathname]);

  const policies = policiesPromise && use(policiesPromise);

  const isSimpleStorefront = merchant?.merchantMeta.selectedSetup === "Simple";
  const isPreviewSite =
    typeof window !== "undefined" &&
    window.location.hostname.startsWith("preview-");
  const isStandaloneGiftCard =
    merchant?.isStandaloneGiftCardWebsite && !isPreviewSite;
  const service = merchant?.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );

  const shouldShowMerchantFooter =
    !isSimpleStorefront &&
    !isCheckoutPage &&
    !isProfilePage &&
    !isConfirmationPage &&
    !isOrderHistoryPage &&
    !isStandaloneGiftCard;

  isRunningTabsEnabled = merchant?.isRunningTabsEnabled
    ? merchant?.isRunningTabsEnabled
    : false;

  const getGoogleLinks = (type: "terms" | "privacy") => {
    return `https://policies.google.com/${type}?hl=${getLocale()}`;
  };

  const getLocale = () => {
    if (localizationEnabledFlag && rawLocale === "fr-CA") {
      return "fr";
    }

    return "en";
  };

  return (
    <div
      data-testid={applyDataTestId(pom.footer.footer)}
      className="w-full bg-[#F8F8F8]"
    >
      {merchant && shouldShowMerchantFooter && (
        <div className="flex-1 flex flex-col w-full mx-auto">
          <MerchantFooter
            merchant={merchant}
            merchantName={merchant.name}
            merchantAddress={merchant.address}
            merchantPhoneNumber={merchant.phone}
            merchantEmail={merchant.customerContactEmail}
            isBusinessHourAvailable={merchant.isBusinessHourAvailable}
            businessHours={
              merchant.isRestaurant
                ? service?.hours
                : merchant?.businessHours?.hours
            }
            facebookUrl={merchant.merchantMeta.socialMediaFacebookUrl}
            instagramUrl={merchant.merchantMeta.socialMediaInstagramUrl}
            timezone={merchant.timezone}
            showLinks={policies?.showLinks}
            locale={merchant.locale}
          />
        </div>
      )}

      {!isCmaApp && (
        <div
          data-testid={applyDataTestId(pom.footer.cloverFooter)}
          className={classNames.join(" ")}
        >
          <div className="w-full border-t-[1px] border-[#727272]"></div>
          <div className="flex flex-col sm:flex-row justify-between">
            <div className=" flex flex-col items-start content-center mb-6 sm:w-3/5">
              <CloverIcon />
              <div className="flex flex-row items-start mt-8 sm:mt-6">
                <div className="text-left sm:text-center text-[15px]">
                  <span>
                    <a
                      href={links.poweredBy}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-[15px]"
                    >
                      {t("poweredBy", { year: new Date().getFullYear() })}
                    </a>
                  </span>
                  &nbsp;&#8226;&nbsp;
                  <span>
                    <a
                      href={links.terms}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-[15px]"
                    >
                      {t("cloverTerms")}
                    </a>
                  </span>
                  &nbsp;&#8226;&nbsp;
                  <span>
                    <a
                      href={links.privacy}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-[15px]"
                    >
                      {t("cloverPrivacyPolicy")}
                    </a>
                  </span>
                </div>
              </div>
              <p
                data-testid={applyDataTestId(pom.footer.description)}
                className="text-left font-normal text-[15px]"
              >
                {t.rich("trademarks", {
                  googlePrivacyPolicyLink: (chunks) => (
                    <a
                      href={getGoogleLinks("privacy")}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-[15px]"
                    >
                      {chunks}
                    </a>
                  ),
                  termsOfServiceLink: (chunks) => (
                    <a
                      href={getGoogleLinks("terms")}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-[15px]"
                    >
                      {chunks}
                    </a>
                  ),
                })}
              </p>
            </div>

            <div className="flex flex-col justify-start sm:justify-between items:start sm:items-end mb-6 gap-5">
              {merchant?.isRestaurant && merchant?.locale === "en-US" && (
                <div className="flex flex-col w-full justify-center items-start sm:items-end">
                  <p className="text-left text-[15px]">{t("orderAhead")}</p>
                  <div className="flex flex-row w-full justify-between sm:justify-end">
                    <a
                      href={props.googlePlayUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t("googlePlayButtonAlt")}
                      data-testid={applyDataTestId(pom.footer.googlePlayButton)}
                    >
                      <GooglePlayBadgeIcon
                        className="mt-3 h-14"
                        width={180}
                        height={53}
                      />
                    </a>
                    <a
                      href={props.appStoreUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t("appStoreButtonAlt")}
                      data-testid={applyDataTestId(pom.footer.appStoreButton)}
                    >
                      <AppStoreBadgeIcon
                        className="mt-3 h-14"
                        width={180}
                        height={50}
                      />
                    </a>
                  </div>
                </div>
              )}
              <div className="text-[14px]/5 flex flex-row space-x-3 items-center">
                <LanguageSelector
                  localizationEnabledFlag={localizationEnabledFlag}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
